import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../store/store';
import { logout } from '../store/slices/authSlice';
import { useSystemSettings, defaultSystemSettings } from '../contexts/SystemSettingsContext';
import Footer from './Footer';
import { 
  Search, 
  LogOut, 
  Menu, 
  X,
  LogIn,
  Shield,
  ChevronDown,
  LayoutDashboard,
  User as UserIcon,
  Zap
} from 'lucide-react';

type PageTheme = {
  bgColor: string;
  textColor: string;
  textColorHover: string;
  activeColor: string;
  borderColor: string;
  logoGradient: string;
  buttonStyle: string;
  particleColor: string;
  glowOrbs: { cyan: { opacity: number }, violet: { opacity: number } };
};

const PAGE_THEMES: Record<string, PageTheme> = {
  '/': {
    bgColor: '#0a0a1a',
    textColor: 'text-white/90',
    textColorHover: 'text-white',
    activeColor: 'text-green-400',
    borderColor: 'border-white/10',
    logoGradient: 'from-green-400 to-emerald-400',
    buttonStyle: 'bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600',
    particleColor: 'rgba(34, 197, 94, 0.6)',
    glowOrbs: { cyan: { opacity: 0.3 }, violet: { opacity: 0.2 } }
  },
  '/analytics': {
    bgColor: '#0a0a1a',
    textColor: 'text-white/90',
    textColorHover: 'text-white',
    activeColor: 'text-cyan-400',
    borderColor: 'border-white/10',
    logoGradient: 'from-cyan-400 to-violet-400',
    buttonStyle: 'bg-gradient-to-r from-cyan-500 to-violet-500 hover:from-cyan-600 hover:to-violet-600',
    particleColor: 'rgba(6, 182, 212, 0.6)',
    glowOrbs: { cyan: { opacity: 0.4 }, violet: { opacity: 0.4 } }
  },
  '/search': {
    bgColor: '#0a0a1a',
    textColor: 'text-white/90',
    textColorHover: 'text-white',
    activeColor: 'text-blue-400',
    borderColor: 'border-white/10',
    logoGradient: 'from-blue-400 to-indigo-400',
    buttonStyle: 'bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600',
    particleColor: 'rgba(59, 130, 246, 0.6)',
    glowOrbs: { cyan: { opacity: 0.3 }, violet: { opacity: 0.3 } }
  },
  '/producer-prices': {
    bgColor: '#0a0a1a',
    textColor: 'text-white/90',
    textColorHover: 'text-white',
    activeColor: 'text-yellow-400',
    borderColor: 'border-white/10',
    logoGradient: 'from-yellow-400 to-orange-400',
    buttonStyle: 'bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600',
    particleColor: 'rgba(234, 179, 8, 0.6)',
    glowOrbs: { cyan: { opacity: 0.2 }, violet: { opacity: 0.3 } }
  },
  '/trade-analytics': {
    bgColor: '#0a0a1a',
    textColor: 'text-white/90',
    textColorHover: 'text-white',
    activeColor: 'text-purple-400',
    borderColor: 'border-white/10',
    logoGradient: 'from-purple-400 to-pink-400',
    buttonStyle: 'bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600',
    particleColor: 'rgba(168, 85, 247, 0.6)',
    glowOrbs: { cyan: { opacity: 0.2 }, violet: { opacity: 0.4 } }
  },
  '/visualization': {
    bgColor: '#0a0a1a',
    textColor: 'text-white/90',
    textColorHover: 'text-white',
    activeColor: 'text-emerald-400',
    borderColor: 'border-white/10',
    logoGradient: 'from-emerald-400 to-teal-400',
    buttonStyle: 'bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600',
    particleColor: 'rgba(16, 185, 129, 0.6)',
    glowOrbs: { cyan: { opacity: 0.3 }, violet: { opacity: 0.2 } }
  },
  '/businesses': {
    bgColor: '#0a0a1a',
    textColor: 'text-white/90',
    textColorHover: 'text-white',
    activeColor: 'text-indigo-400',
    borderColor: 'border-white/10',
    logoGradient: 'from-indigo-400 to-blue-400',
    buttonStyle: 'bg-gradient-to-r from-indigo-500 to-blue-500 hover:from-indigo-600 hover:to-blue-600',
    particleColor: 'rgba(99, 102, 241, 0.6)',
    glowOrbs: { cyan: { opacity: 0.3 }, violet: { opacity: 0.3 } }
  },
  '/news': {
    bgColor: '#0a0a1a',
    textColor: 'text-white/90',
    textColorHover: 'text-white',
    activeColor: 'text-amber-400',
    borderColor: 'border-white/10',
    logoGradient: 'from-amber-400 to-orange-400',
    buttonStyle: 'bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600',
    particleColor: 'rgba(245, 158, 11, 0.6)',
    glowOrbs: { cyan: { opacity: 0.2 }, violet: { opacity: 0.3 } }
  },
  '/courses': {
    bgColor: '#0a0a1a',
    textColor: 'text-white/90',
    textColorHover: 'text-white',
    activeColor: 'text-teal-400',
    borderColor: 'border-white/10',
    logoGradient: 'from-teal-400 to-cyan-400',
    buttonStyle: 'bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600',
    particleColor: 'rgba(20, 184, 166, 0.6)',
    glowOrbs: { cyan: { opacity: 0.4 }, violet: { opacity: 0.2 } }
  },
};

function getThemeForPath(pathname: string): PageTheme {
  if (pathname.startsWith('/news')) return PAGE_THEMES['/news'];
  if (pathname.startsWith('/actualities')) return PAGE_THEMES['/news'];
  if (pathname.startsWith('/courses')) return PAGE_THEMES['/courses'];
  return PAGE_THEMES[pathname] ?? PAGE_THEMES['/analytics'];
}

const GuestLayout: React.FC = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [resourcesOpen, setResourcesOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState<string | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const particlesRef = useRef<Array<{ x: number; y: number; vx: number; vy: number }>>([]);
  const animationFrameRef = useRef<number | undefined>(undefined);
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector((state: RootState) => state.auth);
  const { settings } = useSystemSettings();
  
  const siteName = settings?.siteName || defaultSystemSettings.siteName;

  // Track scroll position to adjust navbar opacity
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      setScrolled(scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close resources dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest('[data-resources-menu]')) {
        return;
      }
      setResourcesOpen(false);
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest('[data-user-menu]')) {
        return;
      }
      setUserMenuOpen(null);
    };
    if (userMenuOpen) {
      const timer = setTimeout(() => {
        document.addEventListener('click', handleClickOutside);
      }, 0);
      return () => {
        clearTimeout(timer);
        document.removeEventListener('click', handleClickOutside);
      };
    }
  }, [userMenuOpen]);

  const mainNav = [
    ...(user ? [{ name: 'Dashboard', href: '/dashboard' }] : []),
    { name: 'Analytics', href: '/analytics' },
    { name: 'Businesses', href: '/businesses' },
    { name: 'Courses', href: '/courses' },
    { name: 'Contact Us', href: '/contact' },
  ];

  const resourcesItems = [
    { name: 'Events', href: '/events' },
    { name: 'News', href: '/news' },
    { name: 'Forum', href: '/forum' },
  ];

  const handleLogout = () => {
    dispatch(logout());
  };

  const isActive = (path: string) => location.pathname === path;
  const isResourcesActive = () => ['/events', '/news', '/forum'].some(p => location.pathname.startsWith(p));

  // Harmonisation navbar (transparente) uniquement pour /
  const isHarmonizedPage = location.pathname === '/';
  const showSolidNavbar = scrolled || !isHarmonizedPage;

  const pageTheme = useMemo(() => getThemeForPath(location.pathname), [location.pathname]);

  // Particle animation system (similar to AnalyticsHome) - Updates with theme
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    const particleCount = 40;
    if (particlesRef.current.length === 0) {
      particlesRef.current = Array.from({ length: particleCount }, () => ({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.5,
        vy: (Math.random() - 0.5) * 0.5,
      }));
    }

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const currentTheme = getThemeForPath(location.pathname);

      particlesRef.current.forEach((particle, i) => {
        particle.x += particle.vx;
        particle.y += particle.vy;

        if (particle.x < 0 || particle.x > canvas.width) particle.vx *= -1;
        if (particle.y < 0 || particle.y > canvas.height) particle.vy *= -1;

        particle.x = Math.max(0, Math.min(canvas.width, particle.x));
        particle.y = Math.max(0, Math.min(canvas.height, particle.y));

        ctx.beginPath();
        ctx.arc(particle.x, particle.y, 2, 0, Math.PI * 2);
        ctx.fillStyle = currentTheme.particleColor;
        ctx.fill();

        particlesRef.current.slice(i + 1).forEach((other) => {
          const dx = particle.x - other.x;
          const dy = particle.y - other.y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < 150) {
            ctx.beginPath();
            ctx.moveTo(particle.x, particle.y);
            ctx.lineTo(other.x, other.y);
            const colorMatch = currentTheme.particleColor.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
            if (colorMatch) {
              const [, r, g, b] = colorMatch;
              ctx.strokeStyle = `rgba(${r}, ${g}, ${b}, ${0.2 * (1 - distance / 150)})`;
            } else {
              ctx.strokeStyle = `rgba(6, 182, 212, ${0.2 * (1 - distance / 150)})`;
            }
            ctx.lineWidth = 1;
            ctx.stroke();
          }
        });
      });

      animationFrameRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [location.pathname]);

  return (
    <div className="min-h-screen flex flex-col relative">
      {/* Particle Canvas */}
      <canvas
        ref={canvasRef}
        className="fixed inset-0 pointer-events-none z-0"
        style={{ opacity: 0.3 }}
      />

      {/* Gradient Radial Halo - Dynamic based on theme */}
      <div 
        className="fixed inset-0 pointer-events-none z-0"
        style={{
          background: `radial-gradient(circle at center, ${pageTheme.particleColor.replace('0.6', '0.08')} 0%, transparent 70%)`,
        }}
      />

      {/* Glow Orbs - Dynamic based on theme */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
        <div
          className="absolute rounded-full"
          style={{
            width: '80rem',
            height: '80rem',
            top: '-40rem',
            left: '-40rem',
            background: `radial-gradient(circle, ${pageTheme.particleColor.replace('0.6', '0.2')} 0%, transparent 70%)`,
            filter: 'blur(100px)',
            opacity: pageTheme.glowOrbs.cyan.opacity,
          }}
        />
        <div
          className="absolute rounded-full"
          style={{
            width: '60rem',
            height: '60rem',
            bottom: '-30rem',
            right: '-30rem',
            background: 'radial-gradient(circle, rgba(139, 92, 246, 0.2) 0%, transparent 70%)',
            filter: 'blur(80px)',
            opacity: pageTheme.glowOrbs.violet.opacity,
          }}
        />
      </div>

      {/* Top Navigation - harmonisé avec la photo sur / */}
      <nav 
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          showSolidNavbar 
            ? 'bg-white border-b border-gray-100' 
            : 'bg-gradient-to-b from-black/20 via-black/10 to-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20 sm:h-24 min-h-[5rem]">
            {/* Logo - gauche (décalé à gauche) */}
            <div className="flex-shrink-0 flex items-center" style={{ maxHeight: '5.5rem', transform: 'translateX(-48px)' }}>
              <Link to="/" className="group block flex items-center">
                <img 
                  src="/logolivinglab.png" 
                  alt="Living Lab Logo" 
                  className={`h-14 sm:h-16 md:h-20 max-h-[4.5rem] sm:max-h-[5.5rem] w-auto object-contain transition-all duration-500 group-hover:scale-105 ${
                    !showSolidNavbar ? 'drop-shadow-lg brightness-110' : ''
                  }`}
                  style={!showSolidNavbar ? { filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.4)) brightness(1.1)' } : {}}
                />
              </Link>
            </div>

            {/* Navigation - centrée entre logo et logout */}
            <div className="hidden md:flex flex-1 justify-center items-center md:space-x-1">
                {mainNav.map((item) => {
                  const isActiveItem = isActive(item.href);
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`group relative inline-flex items-center px-4 py-2 rounded-lg text-sm font-semibold ${
                        showSolidNavbar
                          ? isActiveItem ? 'text-black bg-black/5' : 'text-gray-900 hover:text-black hover:bg-black/5'
                          : isActiveItem ? 'text-white' : 'text-white/90 hover:text-white'
                      }`}
                    >
                      <span>{item.name}</span>
                      {isActiveItem && !showSolidNavbar && (
                        <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-white rounded-full"></div>
                      )}
                      {isActiveItem && showSolidNavbar && (
                        <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-black rounded-full"></div>
                      )}
                    </Link>
                  );
                })}

                {/* Resources dropdown */}
                <div className="relative" data-resources-menu>
                  <button
                    type="button"
                    onClick={() => setResourcesOpen(!resourcesOpen)}
                    className={`group relative inline-flex items-center px-4 py-2 rounded-lg text-sm font-semibold ${
                      showSolidNavbar
                        ? isResourcesActive() ? 'text-black bg-black/5' : 'text-gray-900 hover:text-black hover:bg-black/5'
                        : isResourcesActive() ? 'text-white' : 'text-white/90 hover:text-white'
                    }`}
                  >
                    <span>Resources</span>
                    <ChevronDown className={`h-4 w-4 ml-1 transition-transform duration-200 ${resourcesOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {resourcesOpen && (
                    <div className="absolute left-0 mt-1 w-44 py-1 bg-white rounded-lg shadow-lg border border-gray-100 overflow-hidden z-50">
                      {resourcesItems.map((sub) => {
                        const isSubActive = location.pathname.startsWith(sub.href);
                        return (
                          <Link
                            key={sub.name}
                            to={sub.href}
                            className={`block px-4 py-2.5 text-sm ${isSubActive ? 'text-black bg-black/5' : 'text-gray-700 hover:bg-black/5 hover:text-black'}`}
                            onClick={() => setResourcesOpen(false)}
                          >
                            {sub.name}
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

            {/* Right side - Auth / Logout (décalé à droite) */}
            <div className="flex-shrink-0 flex items-center space-x-3" style={{ transform: 'translateX(48px)' }}>
              {user ? (
                <div data-user-menu className="relative">
                  <button
                    type="button"
                    onClick={() => setUserMenuOpen(userMenuOpen === 'user-menu' ? null : 'user-menu')}
                    className="flex items-center space-x-3 rounded-xl px-2 py-1.5 hover:bg-black/5 transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
                  >
                    <div className="h-9 w-9 rounded-full bg-gradient-to-br from-emerald-400 via-emerald-500 to-emerald-600 flex items-center justify-center shadow-md ring-2 ring-emerald-100 hover:ring-emerald-200 transition-all duration-200">
                      <span className="text-sm font-semibold text-white">
                        {user?.name?.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div className="hidden md:block">
                      <p className={`text-sm font-semibold text-left ${showSolidNavbar ? 'text-gray-900' : 'text-white'}`}>{user?.name}</p>
                      <p className={`text-xs text-left ${showSolidNavbar ? 'text-gray-600' : 'text-white/80'}`}>{user?.role}</p>
                    </div>
                    <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${showSolidNavbar ? 'text-gray-500' : 'text-white/70'} ${userMenuOpen === 'user-menu' ? 'rotate-180' : ''}`} />
                  </button>
                  {userMenuOpen === 'user-menu' && (
                    <div className="absolute right-0 top-full mt-2 w-56 rounded-xl bg-white shadow-lg ring-1 ring-black/5 border border-gray-100 py-1 z-50">
                      <div className="px-3 py-2 border-b border-gray-100">
                        <p className="text-sm font-medium text-slate-900">{user?.name}</p>
                        <p className="text-xs text-slate-500 truncate">{user?.email}</p>
                      </div>
                      <button
                        onClick={() => { setUserMenuOpen(null); navigate('/dashboard'); }}
                        className="flex items-center gap-3 w-full px-3 py-2 text-sm text-slate-700 hover:bg-emerald-50 hover:text-emerald-700 transition-colors text-left"
                      >
                        <LayoutDashboard className="h-4 w-4" />
                        Dashboard
                      </button>
                      <button
                        onClick={() => { setUserMenuOpen(null); navigate('/profile'); }}
                        className="flex items-center gap-3 w-full px-3 py-2 text-sm text-slate-700 hover:bg-emerald-50 hover:text-emerald-700 transition-colors text-left"
                      >
                        <UserIcon className="h-4 w-4" />
                        Profile
                      </button>
                      {(user?.role === 'admin' || user?.globalRole === 'admin') && (
                        <button
                          onClick={() => { setUserMenuOpen(null); navigate('/admin'); }}
                          className="flex items-center gap-3 w-full px-3 py-2 text-sm text-slate-700 hover:bg-emerald-50 hover:text-emerald-700 transition-colors text-left"
                        >
                          <Shield className="h-4 w-4" />
                          Admin Panel
                        </button>
                      )}
                      <div className="border-t border-gray-100 my-1" />
                      <button
                        onClick={() => { setUserMenuOpen(null); handleLogout(); }}
                        className="flex items-center gap-3 w-full px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                      >
                        <LogOut className="h-4 w-4" />
                        Logout
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  to="/login"
                  className={`inline-flex items-center px-4 py-2.5 text-sm font-semibold rounded-lg ${
                    showSolidNavbar ? 'bg-emerald-600 text-white hover:bg-emerald-700' : 'bg-white/20 text-white hover:bg-white/30 border border-white/50'
                  }`}
                >
                  <LogIn className="h-4 w-4 mr-2 transition-all duration-500" />
                  Login
                </Link>
              )}

              {/* Mobile menu button */}
              <div className="md:hidden ml-2">
                <button
                  type="button"
                  className={`inline-flex items-center justify-center p-2 rounded-lg focus:outline-none focus:ring-2 ${
                    showSolidNavbar ? 'text-gray-900 hover:text-black hover:bg-black/5 focus:ring-black/20' : 'text-white hover:text-white focus:ring-white/50'
                  }`}
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                >
                  <span className="sr-only">Open main menu</span>
                  {mobileMenuOpen ? (
                    <X className="block h-6 w-6" />
                  ) : (
                    <Menu className="block h-6 w-6" />
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile menu - Smooth transitions */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-gray-200 bg-white">
            <div className="pt-2 pb-3 space-y-1 px-4">
              {mainNav.map((item) => {
                const isActiveItem = isActive(item.href);
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`block pl-4 pr-4 py-3 text-base font-semibold rounded-lg ${
                      isActiveItem ? 'bg-black/5 text-black border-l-4 border-black' : 'text-gray-900 hover:bg-black/5 border-l-4 border-transparent'
                    }`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {item.name}
                  </Link>
                );
              })}

              {/* Mobile: Resources toggle */}
              <div className="border-l-4 border-transparent" data-resources-menu>
                <button
                  type="button"
                  onClick={() => setResourcesOpen(!resourcesOpen)}
                  className="w-full flex items-center justify-between pl-4 pr-4 py-3 text-base font-semibold rounded-lg text-gray-900 hover:bg-black/5"
                >
                  <span>Resources</span>
                  <ChevronDown className={`h-5 w-5 transition-transform duration-200 ${resourcesOpen ? 'rotate-180' : ''}`} />
                </button>
                {resourcesOpen && (
                  <div className="overflow-hidden transition-all duration-200">
                    {resourcesItems.map((sub) => {
                      const isSubActive = location.pathname.startsWith(sub.href);
                      return (
                        <Link
                          key={sub.name}
                          to={sub.href}
                          className={`block pl-8 pr-4 py-2.5 text-sm ${
                            isSubActive ? 'bg-black/5 text-black' : 'text-gray-700 hover:bg-black/5'
                          }`}
                          onClick={() => { setMobileMenuOpen(false); setResourcesOpen(false); }}
                        >
                          {sub.name}
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
              
              {/* Admin Panel Link for Mobile */}
              {user?.role === 'admin' && (
                <button
                  onClick={() => { setMobileMenuOpen(false); navigate('/admin'); }}
                  className="block w-full text-left pl-4 pr-4 py-3 text-base font-medium rounded-lg text-red-700 hover:bg-red-50 border-l-4 border-transparent"
                >
                  Admin Panel
                </button>
              )}
            </div>
          </div>
        )}
      </nav>

      {/* Spacer pour la navbar fixe */}
      {showSolidNavbar && <div className="h-20 sm:h-24 min-h-[5rem]" />}

      {/* Main Content */}
      <main className="flex-1 relative z-10">
        <Outlet />
      </main>

      <Footer />
    </div>
  );
};

export default GuestLayout;
